import { useState } from 'react';
import type { AtualizarParametrosResponse, OrcamentoResponse, ParametroCongelado } from '@triade/contracts';
import { toast } from 'sonner';
import { useAtualizarParametros } from '@/api/orcamentos';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { ShaCopiavel } from './versoes/sha-copiavel';

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? e.message : 'falha de comunicação com a API';
}

/** Agrupa os parametros pela ordem em que a API os devolve (so organiza a exibicao). */
export function agruparParametros(lista: ParametroCongelado[]): Array<[string, ParametroCongelado[]]> {
  const grupos = new Map<string, ParametroCongelado[]>();
  for (const p of lista) grupos.set(p.grupo, [...(grupos.get(p.grupo) ?? []), p]);
  return [...grupos.entries()];
}

export interface ParametrosCongeladosProps {
  /** Versao exibida (rascunho editavel ou congelada). */
  orc: OrcamentoResponse;
  /** orcamento.editar + custo.ver: quem pode atualizar os parametros de um rascunho. */
  podeAtualizar: boolean;
  /** Ha alteracoes nao gravadas: atualizar regravaria o rascunho por cima delas, entao espera o salvar. */
  alteracoesPendentes: boolean;
}

/**
 * Secao "Parametros congelados" do orcamento: lista legivel (valores ja formatados pela API), atualizacao do rascunho
 * com os parametros vigentes (antes x depois antes de confirmar) e o sha256 do PDF guardado, colapsado.
 */
export function ParametrosCongelados({ orc, podeAtualizar, alteracoesPendentes }: ParametrosCongeladosProps) {
  const parametros = orc.parametros ?? [];
  const grupos = agruparParametros(parametros);
  const atualizar = useAtualizarParametros();
  const [previa, setPrevia] = useState<AtualizarParametrosResponse | null>(null);
  const [erroPrevia, setErroPrevia] = useState<string | null>(null);
  const [aberto, setAberto] = useState(false);
  const [erroDialogo, setErroDialogo] = useState<string | null>(null);
  const ocupado = atualizar.isPending;

  async function verMudancas() {
    setErroPrevia(null);
    try {
      const r = await atualizar.mutateAsync({ id: orc.id, n: orc.versaoNumero, confirmar: false });
      setPrevia(r);
      setErroDialogo(null);
      setAberto(true);
    } catch (e) {
      setErroPrevia(mensagemErro(e));
    }
  }

  async function confirmar() {
    setErroDialogo(null);
    try {
      await atualizar.mutateAsync({ id: orc.id, n: orc.versaoNumero, confirmar: true });
      setAberto(false);
      setPrevia(null);
      toast.success('Parâmetros atualizados');
    } catch (e) {
      setErroDialogo(mensagemErro(e));
    }
  }

  const semMudanca = previa !== null && previa.mudancas.length === 0;

  return (
    <section aria-label="Parâmetros congelados" className="rounded-2xl border border-border bg-card px-[18px] py-4 text-small text-ink-secondary">
      <div className="font-semibold text-ink">Parâmetros congelados</div>
      <p className="mt-1">Custos, tabela, condição e despesas usados neste orçamento ficam salvos com ele. Mudanças futuras no cadastro não alteram este cálculo.</p>

      {parametros.length === 0 ? (
        <p className="mt-3 text-ink-muted">Sem parâmetros registrados nesta versão.</p>
      ) : (
        <div className="mt-3 flex flex-col gap-3">
          {grupos.map(([grupo, itens]) => (
            <div key={grupo} data-testid="grupo-parametro">
              <div className="text-caption font-semibold text-ink-muted">{grupo}</div>
              <ul className="mt-1 flex list-none flex-col gap-1.5 p-0">
                {itens.map((p, i) => {
                  // Valor longo (ou com varios tributos separados por ' · '): rotulo em cima e valor em coluna, um tributo por celula.
                  const valorLongo = p.valor.length > 28 || p.valor.includes(' · ');
                  return (
                    <li
                      key={`${p.rotulo}-${i}`}
                      data-testid="parametro"
                      className={cn('border-t border-line-soft pt-1.5 first:border-t-0', valorLongo ? 'flex flex-col gap-1' : 'flex items-start justify-between gap-3')}
                    >
                      <div className="max-w-full shrink-0 [overflow-wrap:normal]">
                        <div className="text-ink-body">{p.rotulo}</div>
                        <div className="text-micro text-ink-muted">{p.origem}</div>
                      </div>
                      {valorLongo ? (
                        <div className="tabular grid w-full min-w-0 grid-cols-1 gap-x-4 font-medium text-ink-body sm:grid-cols-2">
                          {p.valor.split(' · ').map((tributo, j) => (
                            <span key={j} className="tabular min-w-0 break-words">
                              {tributo}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="tabular min-w-0 break-words text-right font-medium text-ink-body">{p.valor}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}

      <p className="tabular mt-3 text-caption text-ink-muted">motor {orc.motorVersao}</p>

      {orc.somenteLeitura ? (
        <p data-testid="aviso-parametros-enviada" className="mt-3 rounded-lg bg-line-soft px-3 py-2 text-caption text-ink-body">
          Versão enviada: os parâmetros ficam como estavam; para usar os de hoje, gere uma nova versão com custos atuais.
        </p>
      ) : (
        podeAtualizar && (
          <div className="mt-3 flex flex-col gap-2">
            <div>
              <Button
                type="button"
                variant="outline"
                data-testid="acao-atualizar-parametros"
                disabled={alteracoesPendentes || ocupado}
                title={alteracoesPendentes ? 'Salve as alterações antes de atualizar os parâmetros' : undefined}
                onClick={() => void verMudancas()}
              >
                Atualizar parâmetros
              </Button>
            </div>
            {erroPrevia && (
              <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-3 py-2 text-caption text-danger-ink">
                {erroPrevia}
              </p>
            )}
          </div>
        )
      )}

      {orc.pdfSha256 && (
        <details className="mt-3 border-t border-border pt-3" data-testid="verificacao-documento">
          <summary className="cursor-pointer font-medium text-primary">Verificação do documento</summary>
          <p className="mt-2 text-caption text-ink-muted">
            O SHA-256 é a impressão digital do PDF enviado ao cliente. Se alguém alterar o arquivo, o código muda. Compare com o do PDF que você recebeu para provar que é o mesmo documento.
          </p>
          <div className="mt-2">
            <ShaCopiavel valor={orc.pdfSha256} />
          </div>
        </details>
      )}

      <Dialog
        open={aberto}
        onOpenChange={(o) => {
          if (!o && !ocupado) {
            setAberto(false);
            setPrevia(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Atualizar com os parâmetros vigentes?</DialogTitle>
            <DialogDescription>Os valores abaixo são os do rascunho hoje e os que ele teria com os parâmetros de hoje. Nada muda até você confirmar.</DialogDescription>
          </DialogHeader>

          {previa && (
            <div className="flex flex-col gap-3">
              {semMudanca ? (
                <p data-testid="sem-mudancas" className="text-small text-ink-body">
                  Nenhum parâmetro mudou desde que o orçamento foi salvo.
                </p>
              ) : (
                <table data-testid="tabela-mudancas" className="w-full border-collapse text-small">
                  <thead>
                    <tr className="text-left text-caption text-ink-muted">
                      <th scope="col" className="py-1.5 font-medium">Parâmetro</th>
                      <th scope="col" className="py-1.5 text-right font-medium">Antes</th>
                      <th scope="col" className="py-1.5 text-right font-medium">Depois</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previa.mudancas.map((m, i) => (
                      <tr key={`${m.rotulo}-${i}`} className="border-t border-line-soft">
                        <td className="py-1.5 pr-2">
                          <div className="text-ink-body">{m.rotulo}</div>
                          <div className="text-micro text-ink-muted">{m.grupo}</div>
                        </td>
                        <td className="tabular py-1.5 text-right">{m.antes}</td>
                        <td className="tabular py-1.5 text-right font-medium">{m.depois}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-border">
                      <th scope="row" className="py-1.5 text-left font-semibold">Total</th>
                      <td className="tabular py-1.5 text-right">{previa.total.antes}</td>
                      <td className="tabular py-1.5 text-right font-semibold">{previa.total.depois}</td>
                    </tr>
                    <tr>
                      <th scope="row" className="py-1.5 text-left font-semibold">Lucro</th>
                      <td className="tabular py-1.5 text-right">{previa.lucro.antes ?? '—'}</td>
                      <td className="tabular py-1.5 text-right font-semibold">{previa.lucro.depois ?? '—'}</td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          {erroDialogo && (
            <p role="alert" data-testid="erro-atualizar-parametros" className="rounded-xl border border-destructive/30 bg-danger-bg px-3 py-2 text-small text-danger-ink">
              {erroDialogo}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" disabled={ocupado} onClick={() => setAberto(false)}>
              Cancelar
            </Button>
            <Button type="button" disabled={!previa || semMudanca || ocupado} onClick={() => void confirmar()}>
              Confirmar e atualizar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
